import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiFileText, FiCalendar, FiScissors, FiShield, FiMail } = FiIcons;

const Terms = () => {
  React.useEffect(() => {
    document.title = "Terms and Conditions - Melissa's Hairdressing Salon";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.content = "Before using our services, please read our terms and conditions. Learn about appointments, services, and client responsibilities.";
    }
  }, []);

  const termsSections = [
    {
      icon: FiCalendar,
      title: "Appointments",
      content: [
        "We ask for notice of cancellation of at least 24 hours. Failure to provide this notice may result in a cancellation fee.",
        "Appointments are confirmed upon booking and payment of any required deposits.",
        "Late arrivals may result in shortened service time or rescheduling at our discretion.",
        "We reserve the right to reschedule appointments due to unforeseen circumstances."
      ]
    },
    {
      icon: FiScissors,
      title: "Services",
      content: [
        "Services are provided by qualified professionals and are tailored to each client's preferences.",
        "The quality of our services is matched by our commitment to using only the best products and techniques.",
        "All services include a consultation to ensure the best possible outcome.",
        "Results may vary based on individual hair type, condition, and maintenance."
      ]
    },
    {
      icon: FiShield,
      title: "Liability",
      content: [
        "While we strive for perfection, we ask clients to communicate any dissatisfaction before leaving the salon so we can address concerns immediately.",
        "Our liability is limited to the cost of the service provided.",
        "Clients with allergies or sensitivities must inform us prior to service.",
        "We are not responsible for reactions to products used with client consent."
      ]
    }
  ];

  return (
    <div className="font-inter">
      {/* Hero Section */}
      <section className="py-20 lg:py-32 bg-gradient-to-br from-rose-50 via-pink-50 to-amber-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="bg-gradient-to-br from-amber-400 to-rose-400 p-4 rounded-full w-20 h-20 mx-auto mb-6 flex items-center justify-center">
              <SafeIcon icon={FiFileText} className="text-white text-3xl" />
            </div>
            <h1 className="font-playfair text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Terms and Conditions
            </h1>
            <h2 className="font-playfair text-2xl md:text-3xl font-semibold text-gray-700 mb-6">
              Welcome to Melissa's Hairdressing Salon
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              Before you start using our services, we ask that you please read and agree to the terms and conditions below.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Terms Sections */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {termsSections.map((section, index) => (
              <motion.div
                key={section.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className="bg-gradient-to-br from-white to-gray-50 p-8 rounded-2xl shadow-lg"
              >
                <div className="flex items-center mb-6">
                  <div className="bg-gradient-to-br from-amber-400 to-rose-400 p-3 rounded-full mr-4">
                    <SafeIcon icon={section.icon} className="text-white text-xl" />
                  </div>
                  <h3 className="font-playfair text-2xl font-bold text-gray-900">
                    {section.title}
                  </h3>
                </div>
                <ul className="space-y-4">
                  {section.content.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start text-gray-600">
                      <div className="w-2 h-2 bg-amber-400 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Terms */}
      <section className="py-20 bg-gradient-to-br from-rose-50 via-pink-50 to-amber-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="font-playfair text-4xl font-bold text-gray-900 mb-6">
              Additional Terms
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                title: "Payment Policy",
                points: [
                  "Payment is due at the time of service",
                  "We accept cash, credit cards, and digital payments",
                  "Deposits may be required for certain services",
                  "Prices are subject to change with notice"
                ]
              },
              {
                title: "Health & Safety",
                points: [
                  "Please inform us of any allergies or medical conditions",
                  "We maintain strict sanitation and hygiene standards",
                  "Tools and equipment are properly sterilized between clients",
                  "We reserve the right to refuse service for health reasons"
                ]
              },
              {
                title: "Client Conduct",
                points: [
                  "We expect respectful behavior towards staff and other clients",
                  "Mobile phones should be on silent during services",
                  "Children must be supervised at all times",
                  "We reserve the right to refuse service for inappropriate behavior"
                ]
              },
              {
                title: "Satisfaction Guarantee",
                points: [
                  "We strive for 100% client satisfaction",
                  "Please communicate concerns immediately",
                  "Complimentary adjustments available within 48 hours",
                  "Refunds considered on a case-by-case basis"
                ]
              }
            ].map((section, index) => (
              <motion.div
                key={section.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="bg-white p-6 rounded-2xl shadow-lg"
              >
                <h3 className="font-playfair text-xl font-semibold text-gray-900 mb-4">
                  {section.title}
                </h3>
                <ul className="space-y-2">
                  {section.points.map((point, pointIndex) => (
                    <li key={pointIndex} className="flex items-start text-gray-600 text-sm">
                      <div className="w-1.5 h-1.5 bg-amber-400 rounded-full mr-2 mt-2 flex-shrink-0"></div>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Acceptance Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-gradient-to-br from-amber-50 to-rose-50 p-8 rounded-2xl text-center"
          >
            <h3 className="font-playfair text-2xl font-bold text-gray-900 mb-4">
              Acceptance
            </h3>
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              By using services from Melissa's Hairdressing Salon, you agree to be bound by these terms and conditions. These terms may be updated from time to time, and continued use of our services constitutes acceptance of any changes.
            </p>
            <p className="text-gray-600">
              <strong>Effective Date:</strong> December 2024
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-gradient-to-r from-amber-600 to-rose-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-white"
          >
            <SafeIcon icon={FiMail} className="text-4xl mx-auto mb-4" />
            <h3 className="font-playfair text-2xl font-bold mb-4">
              Questions About Our Terms?
            </h3>
            <p className="text-lg mb-6 opacity-90">
              For further information or clarification about our terms and conditions, please reach out to us.
            </p>
            <a 
              href="mailto:terms@melissashair.com"
              className="bg-white text-amber-600 px-8 py-3 rounded-full font-medium text-lg hover:bg-gray-100 transition-all inline-block"
            >
              terms@melissashair.com
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Terms;